/**
 * Estimate Operations
 *
 * Platform-agnostic estimate CRUD operations.
 * These functions can be used by both web and mobile apps.
 *
 * @module @prosperia/shared/data/operations/estimates
 */

import {
  getSupabase,
  getCompanyId as sharedGetCompanyId,
  handleSupabaseError,
  withCompanyFilter,
} from '../../api/supabase';

/**
 * Resolve company ID from options or session
 * @private
 */
async function resolveCompanyId(options = {}) {
  const supabase = getSupabase();
  let companyId = options.companyId;

  if (!companyId && options.getCompanyId) {
    companyId = await options.getCompanyId();
  }

  if (!companyId && options.user) {
    companyId = await sharedGetCompanyId(options.user);
  }

  if (!companyId) {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.user) {
      companyId = await sharedGetCompanyId(session.user);
    }
  }

  return companyId;
}

/**
 * Initialize estimate counter for a company based on existing estimates
 * This handles companies that already have estimates but counter wasn't set up
 *
 * @param {string} companyId - Company ID
 * @param {Object} options - Options object
 * @param {number} options.timeout - Timeout in ms (default: 5000)
 * @returns {Promise<number|null>} New counter value or null on error
 */
export async function initializeEstimateCounter(companyId, options = {}) {
  const timeoutMs = options.timeout || 5000;

  try {
    const supabase = getSupabase();
    console.log('[estimates] initializeEstimateCounter starting for company:', companyId);

    const currentYear = new Date().getFullYear();
    const yearStart = `${currentYear}-01-01`;
    const yearEnd = `${currentYear}-12-31`;

    // Create the initialization promise
    const initPromise = (async () => {
      // Fetch estimates from current year to find highest number
      const { data: estimates, error } = await supabase
        .from('estimates')
        .select('estimate_number')
        .eq('company_id', companyId)
        .gte('created_at', yearStart)
        .lte('created_at', yearEnd)
        .order('created_at', { ascending: false })
        .limit(1000);

      if (error) {
        console.error('[estimates] Error fetching estimates for counter init:', error);
        return null;
      }

      // Find the highest estimate number
      let maxNumber = 0;

      if (estimates && estimates.length > 0) {
        estimates.forEach((estimate) => {
          // Parse estimate numbers like "EST-2025-00001"
          const match = estimate.estimate_number?.match(/EST-(\d+)-(\d+)/);
          if (match) {
            const year = parseInt(match[1]);
            const number = parseInt(match[2]);

            // Only count estimates from current year
            if (year === currentYear) {
              maxNumber = Math.max(maxNumber, number);
            }
          }
        });
      }

      // Set counter to max number (will be incremented when generating)
      const newCounter = maxNumber;

      console.log(`[estimates] Found ${estimates?.length || 0} estimates for ${currentYear}`);
      console.log(`[estimates] Highest estimate number: ${maxNumber}`);
      console.log(`[estimates] Setting counter to: ${newCounter}`);

      // Get current company settings
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('settings')
        .eq('id', companyId)
        .single();

      if (companyError) {
        console.error('[estimates] Error fetching company settings:', companyError);
        return null;
      }

      const settings = company?.settings || {};

      // Update company settings with the counter
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          settings: {
            ...settings,
            estimate_counter: newCounter,
          },
        })
        .eq('id', companyId);

      if (updateError) {
        console.error('[estimates] Error updating company counter:', updateError);
        return null;
      }

      console.log('[estimates] Counter initialized successfully!');
      console.log(`[estimates] Next estimate will be: EST-${currentYear}-${(newCounter + 1).toString().padStart(5, '0')}`);

      return newCounter;
    })();

    // Add timeout protection
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Counter initialization timeout')), timeoutMs)
    );

    return await Promise.race([initPromise, timeoutPromise]);
  } catch (error) {
    console.error('[estimates] initializeEstimateCounter error:', error);
    return null;
  }
}

/**
 * Generate the next estimate number for a company.
 *
 * Delegates to the `next_estimate_number` SQL RPC, which atomically
 * increments `companies.settings.estimate_counter` and returns the formatted
 * number. The atomic UPDATE serializes concurrent callers, so two devices
 * saving at the same moment will never collide on the unique
 * (company_id, estimate_number) constraint.
 *
 * @param {string} companyId
 * @returns {Promise<string>} e.g. "EST-2026-00001"
 */
export async function generateEstimateNumber(companyId) {
  if (!companyId) {
    throw new Error('companyId is required to generate an estimate number');
  }
  const { data, error } = await getSupabase().rpc('next_estimate_number', {
    p_company_id: companyId,
  });
  if (error) throw error;
  return data;
}

/**
 * Get all estimates for a company
 *
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (optional, will be resolved if not provided)
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of estimates or empty array on error
 */
export async function getEstimates(options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[estimates] No company ID available for getEstimates');
      return [];
    }

    const { data, error } = await supabase
      .from('estimates')
      .select(`
        *,
        client:clients(name, email, phone, sms_consent, marketing_sms_consent)
      `)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[estimates] Error fetching estimates:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[estimates] getEstimates error:', error);
    handleSupabaseError(error, 'fetch estimates');
    return [];
  }
}

/**
 * Get a single estimate by ID
 *
 * @param {string} id - Estimate ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Estimate data or null on error
 */
export async function getEstimate(id, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[estimates] No company ID available for getEstimate');
      return null;
    }

    const { data, error } = await withCompanyFilter(
      supabase
        .from('estimates')
        .select(`
          *,
          client:clients(name, email, phone, sms_consent, marketing_sms_consent)
        `)
        .eq('id', id),
      companyId
    ).single();

    if (error) throw error;
    return data;
  } catch (error) {
    handleSupabaseError(error, 'get estimate');
    return null;
  }
}

/**
 * Get estimates by status
 *
 * @param {string} status - Estimate status (draft, sent, viewed, accepted, declined)
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Function} options.getCompanyId - Function to get company ID from context
 * @returns {Promise<Array>} Array of estimates or empty array on error
 */
export async function getEstimatesByStatus(status, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[estimates] No company ID available for getEstimatesByStatus');
      return [];
    }

    const { data, error } = await supabase
      .from('estimates')
      .select(`
        *,
        client:clients(name, email, phone, sms_consent, marketing_sms_consent)
      `)
      .eq('company_id', companyId)
      .eq('status', status)
      .order('created_at', { ascending: false })
      .limit(500);

    if (error) {
      console.error('[estimates] Error fetching estimates by status:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[estimates] getEstimatesByStatus error:', error);
    handleSupabaseError(error, 'fetch estimates by status');
    return [];
  }
}

/**
 * Get estimates by client ID
 *
 * @param {string} clientId - Client ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @param {Function} options.getCachedOrRateLimited - Caching function (optional)
 * @returns {Promise<Array>} Array of estimates or empty array on error
 */
export async function getEstimatesByClient(clientId, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.log('[estimates] No company ID available for getEstimatesByClient');
      return [];
    }

    const fetchEstimates = async () => {
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          client:clients(name, email, phone, sms_consent, marketing_sms_consent)
        `)
        .eq('company_id', companyId)
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('[estimates] Error fetching estimates by client:', error);
        throw error;
      }

      return data || [];
    };

    // Use caching if provided, otherwise fetch directly
    if (options.getCachedOrRateLimited) {
      const cacheKey = `estimates_client_${clientId}_${companyId}`;
      return await options.getCachedOrRateLimited(cacheKey, fetchEstimates);
    }

    return await fetchEstimates();
  } catch (error) {
    console.error('[estimates] getEstimatesByClient error:', error);
    handleSupabaseError(error, 'fetch estimates by client');
    return [];
  }
}

/**
 * Sanitize estimate data - only include allowed database columns
 * @private
 */
function sanitizeEstimateData(data) {
  const allowedKeys = new Set([
    'company_id',
    'client_id',
    'job_id',
    'estimate_number',
    'estimate_type',
    'title',
    'description',
    'customer_name',
    'customer_email',
    'customer_phone',
    'customer_address',
    'items',
    'tax_rate',
    'discount_amount',
    'discount_type',
    'payment_terms',
    'status',
    'requires_signature',
    'valid_until',
    'custom_branding',
    'subtotal',
    'tax_amount',
    'final_price',
    'total_amount',
    'services_subtotal',
    'client_payment_amount',
    'processing_fees_amount',
    'notes',
    'deposit_amount',
    'deposit_type',
    'google_maps_data',
    'map_snapshot_url',
    'signature_data',
    'pdf_url',
    'public_token',
    'created_by',
    'make_line_items_optional',
    'allow_client_scheduling',
    'client_scheduled_at',
    'final_terms',
    'sent_at',
    'viewed_at',
    'accepted_at',
  ]);

  const sanitized = {};
  for (const key of Object.keys(data)) {
    if (allowedKeys.has(key)) {
      sanitized[key] = data[key];
    }
  }
  return sanitized;
}

/**
 * Create a new estimate
 *
 * @param {Object} estimateData - Estimate data to create
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID (required)
 * @param {boolean} options.generateNumber - Auto-generate estimate number if not provided (default: true)
 * @param {Object} options.user - Pre-resolved user (skips getSession lookup)
 * @param {number} options.insertTimeout - Timeout for insert operation (default: 10000ms)
 * @returns {Promise<Object>} Created estimate data
 * @throws {Error} If authentication fails or timeout occurs
 */
export async function createEstimate(estimateData, options = {}) {
  const insertTimeoutMs = options.insertTimeout || 10000;
  const shouldGenerateNumber = options.generateNumber !== false;

  console.log('[estimates] Starting createEstimate...');

  try {
    const supabase = getSupabase();

    // Resolve user from caller, otherwise fall back to getSession (no timeout
    // race — see clients.createClient for rationale; the supabase-js auth
    // navigator lock can briefly block concurrent calls and a 5s race here
    // produces false "auth timed out" errors for already-authenticated users).
    let sessionUser = options.user || null;

    if (!sessionUser) {
      console.log('[estimates] No user passed, calling getSession...');
      const {
        data: { session },
        error: authError,
      } = await supabase.auth.getSession();

      if (authError) {
        console.error('[estimates] Auth error:', authError);
        throw new Error(`Authentication error: ${authError.message}`);
      }

      if (!session?.user) {
        throw new Error('User authentication required to create estimate. Please sign in again.');
      }

      sessionUser = session.user;
    }

    // Resolve company ID
    let companyId = options.companyId || estimateData.company_id;
    if (!companyId) {
      companyId = await sharedGetCompanyId(sessionUser);
    }

    if (!companyId) {
      throw new Error('No company ID found');
    }

    // Sanitize and prepare data
    const sanitized = sanitizeEstimateData(estimateData);

    // Generate estimate number if not provided
    let estimateNumber = sanitized.estimate_number;
    if (!estimateNumber && shouldGenerateNumber) {
      estimateNumber = await generateEstimateNumber(companyId);
      console.log('[estimates] Generated estimate number:', estimateNumber);
    }

    const estimateDataWithUser = {
      ...sanitized,
      company_id: companyId,
      estimate_number: estimateNumber,
      created_by: sessionUser.id,
    };

    console.log('[estimates] Inserting estimate into database...');

    // Add timeout to the insert operation
    const insertTimeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error(`Database insert timed out after ${insertTimeoutMs / 1000} seconds`)),
        insertTimeoutMs
      );
    });

    const { data, error } = await Promise.race([
      supabase.from('estimates').insert([estimateDataWithUser]).select().single(),
      insertTimeout,
    ]);

    console.log('[estimates] Insert result:', { dataId: data?.id, error });

    if (error) {
      console.error('[estimates] Database error:', error);
      handleSupabaseError(error, 'create estimate');
      throw error;
    }

    if (!data) {
      throw new Error('No data returned from database');
    }

    console.log('[estimates] Estimate created successfully:', data.id);
    return data;
  } catch (error) {
    console.error('[estimates] Error in createEstimate:', error);
    throw error;
  }
}

/**
 * Update an existing estimate
 *
 * @param {string} id - Estimate ID
 * @param {Object} updates - Fields to update
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<Object|null>} Updated estimate data or null on error
 */
export async function updateEstimate(id, updates, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[estimates] No company ID available for updateEstimate');
      throw new Error('No company ID found');
    }

    // Validate estimate ID
    if (!id || typeof id !== 'string' || id.length < 30) {
      console.error('[estimates] Invalid estimate ID:', id);
      throw new Error(`Invalid estimate ID: ${id}`);
    }

    // Sanitize update data - don't include company_id in updates
    const sanitized = sanitizeEstimateData(updates);
    delete sanitized.company_id; // Never update company_id

    console.log('[estimates] Updating estimate:', id, 'with data:', sanitized);

    const { data, error } = await withCompanyFilter(
      supabase.from('estimates').update(sanitized).eq('id', id),
      companyId
    )
      .select()
      .single();

    if (error) {
      console.error('[estimates] Update error:', error);
      throw error;
    }

    console.log('[estimates] Update successful:', data?.id);
    return data;
  } catch (error) {
    handleSupabaseError(error, 'update estimate');
    return null;
  }
}

/**
 * Delete an estimate
 *
 * @param {string} id - Estimate ID
 * @param {Object} options - Options object
 * @param {string} options.companyId - Company ID for filtering
 * @param {Object} options.user - User object for company ID resolution
 * @returns {Promise<boolean>} True on success, false on error
 */
export async function deleteEstimate(id, options = {}) {
  try {
    const supabase = getSupabase();
    const companyId = await resolveCompanyId(options);

    if (!companyId) {
      console.error('[estimates] No company ID available for deleteEstimate');
      throw new Error('No company ID found');
    }

    const { error } = await withCompanyFilter(
      supabase.from('estimates').delete().eq('id', id),
      companyId
    );

    if (error) throw error;
    return true;
  } catch (error) {
    handleSupabaseError(error, 'delete estimate');
    return false;
  }
}
